import { createContext, useContext, type ReactElement } from 'react'
import { Drawer, Modal } from '@mantine/core'
import { useIsMobile } from '@/lib/mobileDetector'

const DialogKindContext = createContext<'drawer' | 'modal'>('modal')

/**
 * Names the drawer or modal after a heading inside `content`, for dialogs that draw their own header
 * instead of passing `title`. The render function receives the id Mantine links with aria-labelledby.
 */
export function DrawerOrModalTitle({ children }: { children: (id: string) => ReactElement }) {
    const kind = useContext(DialogKindContext)
    const DialogTitle = kind === 'drawer' ? Drawer.Title : Modal.Title
    return <DialogTitle renderRoot={({ id }) => children(id)} />
}

interface DrawerOrModalContentProps {
    content: React.ReactNode
    showContent: boolean
    setShowContent: (showContent: boolean) => void
    onClose: () => void
    title?: React.ReactNode
    withCloseButton?: boolean
    size?: string
    /** Keeps footer actions visible; only the inner scroll region moves. */
    pinFooter?: boolean
}

export default function DrawerOrModalContent({
    content,
    showContent,
    onClose,
    setShowContent,
    title,
    withCloseButton = true,
    size = '50%',
    pinFooter = false,
}: DrawerOrModalContentProps) {
    const handleClose = () => {
        setShowContent(false)
        onClose()
    }

    const isMobile = useIsMobile()
    if (isMobile) {
        return (
            <Drawer
                bg="var(--gm-gray-9)"
                opened={showContent}
                onClose={handleClose}
                position="bottom"
                withOverlay={true}
                overlayProps={{ opacity: 0.5, blur: 5 }}
                withCloseButton={withCloseButton}
                title={title}
                closeButtonProps={{ size: 32, p: 4, 'aria-label': 'Close' }}
                data-testid="drawer-modal-mobile"
                styles={{
                    title: {
                        paddingRight: '2px',
                    },
                    header: {
                        paddingBottom: 'var(--gm-spacing-12)',
                        paddingRight: 'var(--gm-spacing-12)',
                        paddingLeft: 'var(--gm-spacing-24)',
                    },
                    body: pinFooter
                        ? {
                              padding: 0,
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              flex: 1,
                              minHeight: 0,
                          }
                        : {
                              paddingLeft: 'var(--gm-spacing-16)',
                              overflowY: 'auto',
                          },
                    content: {
                        height: 'auto',
                        maxHeight: size,
                        ...(pinFooter && {
                            display: 'flex',
                            flexDirection: 'column',
                        }),
                    },
                }}
                zIndex={3000}
            >
                <DialogKindContext.Provider value="drawer">{content}</DialogKindContext.Provider>
            </Drawer>
        )
    }

    return (
        <Modal
            opened={showContent}
            onClose={handleClose}
            centered
            withCloseButton={withCloseButton}
            title={title}
            closeButtonProps={{ size: 36, p: 6, 'aria-label': 'Close' }}
            data-testid="modal-desktop"
            zIndex={3000}
            styles={
                pinFooter
                    ? {
                          content: {
                              display: 'flex',
                              flexDirection: 'column',
                              maxHeight: size,
                          },
                          body: {
                              padding: 0,
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              flex: 1,
                              minHeight: 0,
                          },
                      }
                    : undefined
            }
        >
            {content}
        </Modal>
    )
}
